import {
  Credenza,
  CredenzaBody,
  CredenzaClose,
  CredenzaContent,
  CredenzaDescription,
  CredenzaFooter,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaTrigger,
} from '@/components/ui/credenza';
import { cn } from '@/utils/utils.ts';
import { Funnel } from 'lucide-react';
import { useGetPlantsQuery } from '@/features/plants/hooks/queries/use-get-plants.tsx';
import { useEventsFilterStore } from '@/services/store/use-events-store.ts';
import { useCallback, useMemo, useState } from 'react';

import { Label } from '@/components/ui/label.tsx';
import { MultiSelect } from '@/components/ui/multi-select.tsx';
import { Button } from '@/components/ui/button';

export function EventsFilterDialog() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  return (
    <Credenza open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <CredenzaTrigger asChild>
        <button
          className={cn(
            'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm',
            'text-left',
            'hover:bg-accent hover:text-accent-foreground',
            'focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:outline-none',
            'disabled:pointer-events-none disabled:opacity-50'
          )}
        >
          <Funnel className="h-4 w-4" /> Filter
        </button>
      </CredenzaTrigger>
      <EventsFilterDialogContent closeDialog={closeDialog} />
    </Credenza>
  );
}

function EventsFilterDialogContent({ closeDialog }: { closeDialog?: () => void }) {
  const sourceFilters = useEventsFilterStore((state) => state.sourceFilters);
  const setSourceFilters = useEventsFilterStore((state) => state.setSourceFilters);

  const initialValues = useMemo(
    () => sourceFilters.map(({ sourceId }) => sourceId),
    [sourceFilters]
  );

  const [selectedPlantIds, setSelectedPlantIds] = useState<string[]>(initialValues);

  const { data: plants, isLoading } = useGetPlantsQuery();

  const plantOptions = useMemo(
    () =>
      plants?.map((plant) => ({
        value: plant.plantId,
        label: plant.basicDetails.name,
        source: 'plant' as const,
      })),
    [plants]
  );

  const handleApplyFilters = useCallback(() => {
    if (!plants) return;

    const newFilters = selectedPlantIds.map((plantId) => {
      const plant = plants.find((p) => p.plantId === plantId);
      return {
        sourceId: plantId,
        source: 'plant' as const,
        label: plant?.basicDetails.name ?? 'Unknown Plant',
      };
    });

    setSourceFilters(newFilters);
    closeDialog?.();
  }, [selectedPlantIds, plants, setSourceFilters, closeDialog]);

  return (
    <CredenzaContent>
      <CredenzaHeader>
        <CredenzaTitle>Filter Events</CredenzaTitle>
        <CredenzaDescription>
          Select plants to filter events. Only events from selected plants will be displayed.
        </CredenzaDescription>
      </CredenzaHeader>
      <CredenzaBody>
        <div className="flex flex-col gap-2">
          <Label>Plants</Label>
          <MultiSelect
            options={plantOptions ?? []}
            onValueChange={setSelectedPlantIds}
            defaultValue={selectedPlantIds}
            placeholder={isLoading ? 'Loading plants...' : 'Select plants'}
            variant="inverted"
            maxCount={3}
            disabled={isLoading}
          />
        </div>
      </CredenzaBody>
      <CredenzaFooter className="sm:gap-4">
        <Button onClick={handleApplyFilters}>Filter</Button>
        <CredenzaClose asChild>
          <button>Close</button>
        </CredenzaClose>
      </CredenzaFooter>
    </CredenzaContent>
  );
}
